/* eslint-disable no-underscore-dangle */
import { AppBar, Badge, Container, Icon, IconButton, makeStyles, Popover, Toolbar, Tooltip } from '@material-ui/core';
import { ExitToApp, InfoOutlined, Settings, ShoppingCart } from '@material-ui/icons';
import GavelIcon from '@material-ui/icons/Gavel';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { Link, useLocation, useMatch, useNavigate } from '@reach/router';
import clsx from 'clsx';
import { isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { RoutePaths } from 'src/constants';
import ImprintPopup from 'src/containers/ImprintPopup';
import { useCustomerMode } from 'src/hooks/useCustomerMode';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { getSourceOptions, useSources } from 'src/hooks/useSources';
import { useUser } from 'src/hooks/useUser';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import { useValuationSettings } from 'src/hooks/useValuationSettings';
import { notificationState } from 'src/stores/state';

import insightIcon from '../assets/icons/insights.svg';
import AllucLogoImage from '../assets/images/Allmobil_Logo.jpg';
import logoImage from '../assets/images/logo.png';
import vgrdLogoImage from '../assets/images/vgrd-logo.svg';
import SupportPopup from '../containers/SupportPopup';
import useRole from '../hooks/useRole';
import { useWizard } from '../hooks/useWizard';
import { ConfigNavigationItems } from '../modules/data';
import { CountryCode, SourceType, ValuationCountryCode, WizardStep } from '../modules/generated/api';
import { GwscoutJwtPayload, useAuthenticationStore } from '../stores/Authentication';

import CustomerModeToggle from './CustomerModeToggle';
import GavelClockIcon from './GavelClockIcon';
import LanguageCountrySwitcher from './LanguageCountrySwitcher';
import LanguageSwitcher from './LanguageSwitcher';
import NavigationLink, { useStyles as useStylesButton } from './NavigationLink';
import NavigationLinkDropdown from './NavigationLinkDropdown';
import { filterNotifications } from './notification/notification-helpers';
import NotificationPopup from './notification/NotificationPopup';
import { AppNotification, NotificationType } from './notification/types';
import { useImportantNotification, useNotImportantNotification } from './notification/useAllNotification';
import RoleSimulationChip from './RoleSimulationChip';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    zIndex: 1001,
    position: 'sticky',
    top: 0,
  },
  toolBar: {
    padding: 0,
  },
  title: {
    display: 'flex',
    alignItems: 'flex-end',
    color: 'inherit',
    textDecoration: 'none',
    marginTop: -6,
  },
  logo: {
    display: 'inline-block',
    width: 240,
    height: 'auto',
    marginRight: theme.spacing(2),
  },
  logo2: {
    display: 'inline-block',
    width: 200,
    height: 'auto',
    marginRight: theme.spacing(2),
  },
  titleTypography: {
    lineHeight: 1,
    fontWeight: 'bold',
    fontSize: '2.5rem',
    fontVariant: 'small-caps',
    padding: `0px ${theme.spacing(2)}px`,
    textDecorationLine: 'underline',
    textDecorationColor: theme.palette.primary.main,
    marginBottom: -6,
  },
  links: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  printHide: {
    '@media print': {
      display: 'none',
    },
  },
  singleIcon: {
    width: '3em',
    height: 'auto',
    fontWeight: 'bold',
  },
  pendingBidNav: {
    paddingRight: theme.spacing(0.8),
    paddingTop: theme.spacing(0.8),
    paddingBottom: theme.spacing(0.8),
  },
  pastBidNav: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingTop: theme.spacing(0.8),
    paddingBottom: theme.spacing(0.8),
  },
  boughtBidNav: {
    paddingLeft: theme.spacing(1),
    paddingTop: theme.spacing(0.8),
    paddingBottom: theme.spacing(0.8),
  },
  gavelIconClock: {
    fontSize: '1.8rem',
  },
  imprint: {
    padding: theme.spacing(0, 2, 0, 1),
  },
  bellIcon: {
    color: 'black',
    margin: theme.spacing(0.3),
  },
  recommendationIcon: {
    width: 24,
    height: 24,
  },
  recommendationIconActive: {
    filter: 'brightness(0) saturate(100%) invert(44%) sepia(60%) saturate(2000%) hue-rotate(233deg) brightness(100%)',
  },
}));

const ConfigurationNavigation = () => {
  const { t } = useTranslation();
  const { getRoles, hasRole } = useRole();
  const roles = getRoles();
  const { data: source = [] } = useSources();
  const sourceOptions = getSourceOptions(source);
  const sourceValues = sourceOptions.map(({ value }) => value);
  const { state: authState } = useAuthenticationStore();
  const dealerCountry = authState.objectToken?.country;
  const { hasWizardStepsToShow } = useWizard();
  const { customerModeAvailable, customerModeActive } = useCustomerMode();

  const allowedConfNavItems = ConfigNavigationItems.filter((navItem) => {
    const roleAllowed = roles.some((role) => navItem.role.includes(role));
    const countryAllowed =
      isUndefined(navItem.hideForCountries) ||
      (dealerCountry !== undefined && !navItem.hideForCountries?.includes(dealerCountry as CountryCode)) ||
      navItem.ignoreCountryBlacklist?.some((role) => hasRole(role)) ||
      // IT dealers are allowed to see & add credentials only if their allowed Sources are more than only VWFS
      (dealerCountry === CountryCode.It && sourceValues.length > 1 && sourceValues.includes(SourceType.Vwfs));

    return roleAllowed && countryAllowed;
  });

  return (
    <NavigationLinkDropdown
      title={t('navigation.configuration')}
      icon={
        <Badge
          color="primary"
          variant="dot"
          overlap="circular"
          invisible={!hasWizardStepsToShow([WizardStep.TermsConditions])}
        >
          <Settings />
        </Badge>
      }
      items={customerModeActive ? [] : allowedConfNavItems}
    >
      {customerModeAvailable && <CustomerModeToggle context="navigation" />}
    </NavigationLinkDropdown>
  );
};

const MAX_TIMEOUT_MS = 2 ** 31 - 1;

type NotificationNavigationProps = {
  objectToken?: GwscoutJwtPayload;
};
const NotificationNavigation = ({ objectToken }: NotificationNavigationProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const anchorEl = useRef<HTMLDivElement | null>(null);
  const [notificationPopupOpen, setNotificationPopupOpen] = useState(false);
  const [login, setLoginActive] = useState(false);

  const { data: notificationList } = useNotImportantNotification();
  const { data: importantNotification } = useImportantNotification();
  const [notifications, setNotifications] = useRecoilState<AppNotification[]>(notificationState);
  const [onlyUnreadNotifications, setOnlyUnreadNotifications] = useState(true);
  const [scheduleTick, setScheduleTick] = useState(0);
  const { data: user } = useUser();

  useEffect(() => {
    // Combine and filter notifications
    const allNotifications = [...(notificationList || []), ...(importantNotification || [])];
    const filteredNotifications = filterNotifications(allNotifications, user, onlyUnreadNotifications);

    setNotifications(filteredNotifications);

    // Show popup if any important notification is active
    const hasActiveImportant = filteredNotifications.some(
      (n) =>
        n.messageType === NotificationType.Important &&
        (!n.startTime || DateTime.fromISO(n.startTime) <= DateTime.now()),
    );
    if (hasActiveImportant) {
      setNotificationPopupOpen(true);
    }

    // Find the next moment a notification appears, resurfaces or expires
    const nextChange = allNotifications
      .flatMap((n) => [n.startTime, n.updatedAt, n.baseUpdatedAt, n.endTime])
      .map((iso) => iso && DateTime.fromISO(iso))
      .filter((dt): dt is DateTime => !!dt && dt.isValid && dt > DateTime.now())
      .sort((a, b) => a.toMillis() - b.toMillis())[0];

    let timer: NodeJS.Timeout | undefined;
    if (nextChange) {
      // setTimeout overflows above ~24.8 days and would fire immediately, re-running this effect in a loop
      const timeout = Math.min(nextChange.toMillis() - DateTime.now().toMillis() + 100, MAX_TIMEOUT_MS);
      // Re-running the effect re-filters, opens the popup if needed and schedules the next change
      timer = setTimeout(() => setScheduleTick((tick) => tick + 1), timeout);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    notificationList,
    importantNotification,
    setNotifications,
    user,
    onlyUnreadNotifications,
    setNotificationPopupOpen,
    scheduleTick,
  ]);

  // if the iss is within the last 3 minutes set notificationPopupOpen
  useEffect(() => {
    if (objectToken?.iat && DateTime.fromSeconds(objectToken.iat) >= DateTime.now().minus({ minute: 3 })) {
      if (notifications && notifications?.length > 0 && onlyUnreadNotifications) {
        setNotificationPopupOpen(true);
        setLoginActive(true);
      }
    }
  }, [objectToken?.iat, setNotificationPopupOpen, notifications, onlyUnreadNotifications]);

  return (
    <div ref={anchorEl}>
      <IconButton size="small" aria-label="notifications" onClick={() => setNotificationPopupOpen(true)}>
        <Badge
          color="primary"
          variant="dot"
          overlap="circular"
          invisible={notifications.filter((n) => !n.isRead).length === 0}
        >
          <Tooltip title={t('common.notification_plural')}>
            <NotificationsIcon className={classes.bellIcon} />
          </Tooltip>
        </Badge>
      </IconButton>
      <Popover
        open={notificationPopupOpen}
        onClose={() => setNotificationPopupOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <NotificationPopup onlyUnread={onlyUnreadNotifications} showUnread={setOnlyUnreadNotifications} login={login} />
      </Popover>
    </div>
  );
};

const Navigation = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const buttonClasses = useStylesButton();
  const {
    dispatch,
    state: { canUseScout, objectToken },
  } = useAuthenticationStore();
  const location = useLocation();
  const servicePage = location.pathname.includes('services');
  const navigate = useNavigate();
  const { showWizardBadge } = useWizard();

  const matchCarDetail = useMatch(RoutePaths.car);
  const isRecommendationEnabled = useFeatureFlags('irisRecommendation');
  const hasRecommendationPermission = useUserPermissions(UserPermissions.IRIS_RECOMMENDATION);
  const { customerModeActive } = useCustomerMode();
  const { valuationCountry } = useValuationSettings();
  const isVgrdBranding = customerModeActive && valuationCountry === ValuationCountryCode.De;
  // Navigation renders outside <Router>, so it shows on /forbidden, /login and the public service
  // pages too -- where no route guard has vetted the session and it has to decide for itself.

  return (
    <AppBar position="static" color="primary" className={classes.root}>
      <Container maxWidth={false}>
        <Toolbar className={classes.toolBar}>
          <Link to="/" className={classes.title}>
            {!canUseScout && servicePage ? (
              <img alt={t('common.alluc')} src={AllucLogoImage} className={classes.logo2} /> // Different Logo if on Service page and no access to Iris
            ) : (
              <img
                alt={isVgrdBranding ? t('common.vgrd') : t('common.gwscout')}
                src={isVgrdBranding ? vgrdLogoImage : logoImage}
                className={classes.logo}
              />
            )}
          </Link>

          {canUseScout ? (
            <div className={classes.links}>
              {!customerModeActive && <LanguageCountrySwitcher />}
              {!customerModeActive && <RoleSimulationChip />}
              <NavigationLink to={RoutePaths.cars} text={t('common.car_plural')}>
                <Icon className={classes.singleIcon}>
                  <img
                    src={
                      // eslint-disable-next-line no-nested-ternary
                      location.pathname.startsWith(RoutePaths.cars) && !matchCarDetail
                        ? isVgrdBranding
                          ? '/images/icons/multiple_cars_highlight_vgrd.svg'
                          : '/images/icons/multiple_cars_highlight.svg'
                        : '/images/icons/multiple_cars_icon.svg'
                    }
                    alt={t('common.car_plural')}
                  />
                </Icon>
              </NavigationLink>

              {!customerModeActive && (
                <NavigationLink
                  to={RoutePaths.bidsPending}
                  text={t('bids.pendingBids_plural')}
                  className={classes.pendingBidNav}
                >
                  <GavelClockIcon className={classes.gavelIconClock} />
                </NavigationLink>
              )}

              {!customerModeActive && (
                <NavigationLink
                  to={RoutePaths.bidsPast}
                  text={t('bids.sentBids_plural')}
                  className={classes.pastBidNav}
                >
                  <GavelIcon />
                </NavigationLink>
              )}

              {!customerModeActive && (
                <NavigationLink to={RoutePaths.orders} text={t('bids.boughtCars')} className={classes.boughtBidNav}>
                  <ShoppingCart />
                </NavigationLink>
              )}

              {!customerModeActive && <NotificationNavigation objectToken={objectToken} />}

              {!customerModeActive && isRecommendationEnabled && hasRecommendationPermission && (
                <NavigationLink to={RoutePaths.insight} text={t('navigation.insight')}>
                  <Icon>
                    <img
                      src={insightIcon}
                      alt={t('navigation.insight')}
                      className={clsx(classes.recommendationIcon, {
                        [classes.recommendationIconActive]: location.pathname.startsWith(RoutePaths.insight),
                      })}
                    />
                  </Icon>
                </NavigationLink>
              )}

              <ConfigurationNavigation />

              <NavigationLinkDropdown
                title={t('navigation.information')}
                icon={
                  <Badge
                    color="primary"
                    variant="dot"
                    overlap="circular"
                    invisible={!showWizardBadge(WizardStep.TermsConditions)}
                  >
                    <InfoOutlined />
                  </Badge>
                }
                items={[
                  {
                    title: t('navigation.help'),
                    to: '/help',
                  },
                  {
                    title: t('termsAndConditions.termsAndConditions_short'),
                    to: RoutePaths.termsAndConditions,
                    wizardKey: WizardStep.TermsConditions,
                  },
                  {
                    title: t('navigation.imprint'),
                    to: RoutePaths.imprint,
                  },
                ]}
              />

              <Tooltip
                title={`${t('account.logout')}:\n${objectToken?.first_name} ${objectToken?.last_name}\n${
                  objectToken?.email
                }`}
              >
                <IconButton
                  color="inherit"
                  className={buttonClasses.root}
                  onClick={async () => {
                    // Drop the session first
                    dispatch({ type: 'authenticationStore/SIGN_OUT' });
                    await navigate(RoutePaths.logout);
                  }}
                >
                  <ExitToApp />
                </IconButton>
              </Tooltip>
            </div>
          ) : (
            <div className={clsx(classes.links, classes.printHide)}>
              <SupportPopup className={buttonClasses.root} />
              <ImprintPopup className={classes.imprint} />
              <LanguageSwitcher />
            </div>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default memo(Navigation);
